import {
  isDtoolsSiQuoteLocked,
  isQuoteDraftEditable,
  QUERY_KEYS,
  updateRecord,
  useChangesSummary,
  useQuoteDetailsSummary,
  useQuoteQuery,
} from '@/lib/api'
import type { QuoteData } from '@/lib/api/quotes'
import { isQueryLoaded } from '@/lib/plugins/plugin-readiness'
import { usePluginMutation } from '@/lib/plugins/use-plugin-mutation'
import { usePluginQueryKeysToInvalidateStore } from '@/lib/store'
import { QuoteType } from '@/lib/types'
import { useQueryClient } from '@tanstack/react-query'
import Decimal from 'decimal.js'
import { memo, useCallback, useEffect, useMemo } from 'react'
import { quoteAggregatePayload } from '@/lib/quote-aggregates'
import { publishProposalDataChanged } from '@/lib/proposal-data-sync'
import { canWriteQuoteAggregates, quoteAggregateReadinessKey } from '../quote-tax-policy'

// `null`/`undefined` server fields (never written) compare equal to a 0 summary —
// intentional, since both represent "no value stored".
const eq = (a: number | null | undefined, b: number | null | undefined) =>
  new Decimal(a ?? 0).toDecimalPlaces(2).equals(new Decimal(b ?? 0).toDecimalPlaces(2))

export const QuotePlugin = memo(function QuotePlugin() {
  const { data: quote, dataUpdatedAt: quoteUpdatedAt } = useQuoteQuery()
  const { data: quoteDetailSummary, dataUpdatedAt: quoteDetailSummaryUpdatedAt } = useQuoteDetailsSummary()
  const { data: changesSummary, dataUpdatedAt: changesSummaryUpdatedAt } = useChangesSummary()
  const addQueryKeys = usePluginQueryKeysToInvalidateStore((state) => state.addQueryKeys)
  const queryClient = useQueryClient()

  const isDraft = isQuoteDraftEditable(quote)
  const isChangeOrder = quote?.dsy_type === QuoteType.ChangeOrder
  const isDtoolsSiLocked = isDtoolsSiQuoteLocked(quote)
  const canWriteAggregates = canWriteQuoteAggregates(quote)
  const aggregateReadinessKey = quoteAggregateReadinessKey(quote)

  const summary = useMemo(
    () => (isChangeOrder ? changesSummary : quoteDetailSummary),
    [isChangeOrder, changesSummary, quoteDetailSummary]
  )
  const summaryUpdatedAt = isChangeOrder ? changesSummaryUpdatedAt : quoteDetailSummaryUpdatedAt

  const computeWrites = useCallback(() => {
    if (!quote || !isDraft || isDtoolsSiLocked || !canWriteAggregates) return null

    const quotePayload = quoteAggregatePayload(summary)
    const subtotal = quotePayload.totallineitemamount
    const discountsTotal = quotePayload.totaldiscountamount
    const taxPrice = quotePayload.totaltax
    const total = quotePayload.totalamount

    const quoteDirty =
      !eq(quote.msdyn_estimatedcost, quotePayload.msdyn_estimatedcost) ||
      !eq(quote.totallineitemamount, subtotal) ||
      !eq(quote.totaldiscountamount, discountsTotal) ||
      !eq(quote.totaltax, taxPrice) ||
      !eq(quote.totalamount, total) ||
      !eq(quote.dsy_totalhours, quotePayload.dsy_totalhours)

    const oppoId = quote._opportunityid_value
    const shouldWriteOpportunity = !isChangeOrder && !!oppoId
    const opp = quote.opportunityid
    const opportunityDirty =
      shouldWriteOpportunity &&
      (!opp ||
        !eq(opp.totallineitemamount, subtotal) ||
        !eq(opp.totaldiscountamount, discountsTotal) ||
        !eq(opp.estimatedvalue, subtotal) ||
        !eq(opp.totaltax, taxPrice) ||
        !eq(opp.totalamount, total))

    return {
      quoteDirty,
      opportunityDirty,
      oppoId,
      shouldWriteOpportunity,
      payloads: {
        quote: quotePayload,
        opportunity: {
          totallineitemamount: subtotal,
          totaldiscountamount: discountsTotal,
          estimatedvalue: subtotal,
          totaltax: taxPrice,
          totalamount: total,
        },
      },
    }
  }, [quote, isDraft, isDtoolsSiLocked, canWriteAggregates, isChangeOrder, summary])

  const { schedule, cancel } = usePluginMutation({
    flushId: 'quote',
    scopeKey: quote?.quoteid,
    mutationKey: ['plugin', 'quote', 'update-aggregates'],
    fingerprint: () => [quoteDetailSummaryUpdatedAt, changesSummaryUpdatedAt, quoteUpdatedAt],
    isReady: () => isQueryLoaded(summaryUpdatedAt),
    notReadyMessage: 'Quote summary is still loading. Please try activating again in a moment.',
    isDirty: () => {
      const work = computeWrites()
      return !!work && (work.quoteDirty || work.opportunityDirty)
    },
    mutationFn: async () => {
      const work = computeWrites()
      if (!work || !quote) throw new Error('Failed to update quote')

      const promises: Promise<unknown>[] = []
      if (work.quoteDirty) {
        promises.push(
          updateRecord('quotes', quote.quoteid, work.payloads.quote).then(() => {
            // Reflect the write in the cache immediately so a follow-up isDirty
            // check during InvalidatePlugin's debounce + refetch window doesn't
            // compare against stale pre-write values.
            queryClient.setQueryData<QuoteData>(QUERY_KEYS.quote(), (prev) =>
              prev ? { ...prev, ...work.payloads.quote } : prev
            )
          })
        )
      }
      if (work.shouldWriteOpportunity && work.opportunityDirty && work.oppoId) {
        promises.push(
          updateRecord('opportunities', work.oppoId, work.payloads.opportunity).then(() => {
            queryClient.setQueryData<QuoteData>(QUERY_KEYS.quote(), (prev) =>
              prev?.opportunityid
                ? { ...prev, opportunityid: { ...prev.opportunityid, ...work.payloads.opportunity } }
                : prev
            )
          })
        )
      }
      return await Promise.all(promises)
    },
    onSuccess: () => {
      addQueryKeys([QUERY_KEYS.quote()])
      if (quote?.quoteid) publishProposalDataChanged(quote.quoteid)
    },
    onError: (error) => {
      console.error('QuotePlugin: ~ error:', error)
    },
  })

  useEffect(() => {
    if (!quote?.quoteid || !isDraft || isDtoolsSiLocked || !canWriteAggregates) return
    // Skip while summary is still loading — INITIAL_SUMMARY is all zeros and
    // would write zeros over real server values.
    if (!isQueryLoaded(summaryUpdatedAt)) return
    schedule(2000)
    return cancel
  }, [
    summary.totalCost,
    summary.subtotalWithSuppliesAndDiscounts,
    summary.discounts.total,
    summary.tax.price,
    summary.total,
    summary.labor.hours,
    summaryUpdatedAt,
    quote?.quoteid,
    aggregateReadinessKey,
    isDraft,
    isDtoolsSiLocked,
    canWriteAggregates,
    schedule,
    cancel,
  ])

  return null
})
